import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../app-state/Hooks';
import NavBar from '../common/NavBar';
import PageHeader from '../common/PageHeader';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import TextInput from '../common/TextInput/TextInput';
import { ButtonContinue } from '../common/Button/ButtonContinue';
import { scrollToTop } from '../common/HelperMethods';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { Tooltip } from '@mui/material';
import { SiteApi } from '../../api/SiteApi';
import {
  EmailResponse,
  WriteToWithdrawEmailRequest
} from '../common/CommonTypes';

type WithdrawalFormData = {
  name: string;
  surname: string;
  vin: string;
  email: string;
  policyNumber: string;
  phoneNumber: string;
  withdrawalReason: string;
};

type WithdrawalFormErrors = {
  name?: string;
  surname?: string;
  email?: string;
  policyNumber?: string;
  phoneNumber?: string;
};

const WithdrawalPage = (): JSX.Element => {
  const withdrawalPage = useAppSelector((s) => s.content.withdrawalPage);
  const brandName = useAppSelector((s) => s.brand.brandName);
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);

  const { t } = useTranslation();

  const formTitle = withdrawalPage?.title || t('page-title.withdrawal');
  const noticeRichText = withdrawalPage?.noticeRichText ?? '';
  const nameLabel =
    withdrawalPage?.nameLabel || t('personal-details.first-name-label');
  const surnameLabel =
    withdrawalPage?.surnameLabel || t('personal-details.surname-label');
  const vinLabel = withdrawalPage?.vinLabel || t('vehicle-details.vin-label');
  const emailLabel =
    withdrawalPage?.emailLabel || t('personal-details.email-address-label');
  const phoneNumberLabel =
    withdrawalPage?.phoneNumberLabel ||
    t('personal-details.phone-number-label');
  const policyNumberLabel =
    withdrawalPage?.policyNumberLabel ||
    t('general.labels.policy-number-label');
  const withdrawalReasonLabel =
    withdrawalPage?.withdrawalReasonLabel ||
    t('general.labels.withdrawal-reason-label');
  const onSubmitText =
    withdrawalPage?.onSubmitMessage ||
    t('withdrawal.submission-success-message');

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [errors, setErrors] = useState<WithdrawalFormErrors>({});
  const [formData, setFormData] = useState<WithdrawalFormData>({
    name: '',
    surname: '',
    vin: '',
    email: '',
    policyNumber: '',
    phoneNumber: '',
    withdrawalReason: ''
  });

  useEffect(() => scrollToTop(), []);

  const handleInputChange = (
    e: React.FormEvent<HTMLInputElement>,
    field: keyof WithdrawalFormData
  ) => {
    setFormData({
      ...formData,
      [field]: e.currentTarget.value
    });
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors: WithdrawalFormErrors = {};

    if (!formData.name) {
      isValid = false;
      newErrors.name = t('validation.required-field', { fieldName: nameLabel });
    }

    if (!formData.surname) {
      isValid = false;
      newErrors.surname = t('validation.required-field', {
        fieldName: surnameLabel
      });
    }

    if (!formData.email) {
      isValid = false;
      newErrors.email = t('validation.required-field', {
        fieldName: emailLabel
      });
    } else if (!CommonValidation.isValidEmail(formData.email)) {
      isValid = false;
      newErrors.email = t(
        'personal-details.email-invalid-personal-details-validation'
      );
    }

    if (!formData.policyNumber) {
      isValid = false;
      newErrors.policyNumber = t('validation.required-field', {
        fieldName: policyNumberLabel
      });
    }

    if (
      formData.phoneNumber &&
      !CommonValidation.isValidPhone(formData.phoneNumber)
    ) {
      isValid = false;
      newErrors.phoneNumber = t('validation.invalid-field', {
        fieldName: phoneNumberLabel
      });
    }

    setErrors(newErrors);
    return isValid;
  };

  const sendEmail = () => {
    const controller = new AbortController();

    const successCallback = (response: EmailResponse) => {
      if (response.success) {
        setIsFormSubmitted(true);
      } else {
        setIsFormSubmitted(false);
        setSubmitError(true);
        response.errorMessages.forEach((errorMessage) => {
          console.error('Failed to send withdrawal email:', errorMessage);
        });
      }
    };

    const failureCallback = (error: string) => {
      setIsFormSubmitted(false);
      setSubmitError(true);
      console.error('Failed to send withdrawal email:', error);
    };

    const writeToWithdrawEmailRequest: WriteToWithdrawEmailRequest = {
      customerFirstName: formData.name,
      customerSurname: formData.surname,
      vin: formData.vin,
      customerEmail: formData.email,
      policyNumber: formData.policyNumber,
      phoneNumber: formData.phoneNumber,
      reasonForWithdrawal: formData.withdrawalReason,
      brandName: brandName,
      countryCode: countryCode,
      emailTitle: t('withdrawal.submission-email-title', {
        policyNumber: formData.policyNumber
      }),
      emailSubject: t('withdrawal.submission-email-subject', {
        defaultValue: 'Right To Withdraw Request'
      }),
      labels: {
        name: t('withdrawal.email-labels.name', { defaultValue: 'Name' }),
        email: t('withdrawal.email-labels.email', { defaultValue: 'E-mail' }),
        policyNumber: t('withdrawal.email-labels.policy-number', {
          defaultValue: 'Policy Number'
        }),
        vin: t('withdrawal.email-labels.vin', { defaultValue: 'VIN' }),
        phoneNumber: t('withdrawal.email-labels.phone-number', {
          defaultValue: 'Phone Number'
        }),
        reasonForWithdrawal: t('withdrawal.email-labels.withdrawal-reason', {
          defaultValue: 'Reason for Withdrawal'
        }),
        dateOfSubmission: t('withdrawal.email-labels.date-of-submission', {
          defaultValue: 'Date of Submission'
        })
      }
    };

    new SiteApi().sendWriteToWithdrawEmail(
      writeToWithdrawEmailRequest,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const handleSubmit = () => {
    if (!isFormSubmitted && validateForm()) {
      sendEmail();
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.withdrawal')}</title>
      </Helmet>
      <div className='flex-column main-column'>
        <div className='main-content'>
          <NavBar />
          <div className='flex-row'>
            <div className='page-container'>
              <div className='page-header-line' />
              {isFormSubmitted ? (
                <h2>
                  <output id='form-status' aria-live='polite'>
                    {onSubmitText}
                  </output>
                </h2>
              ) : (
                <>
                  <PageHeader title={formTitle} description='' />
                  {noticeRichText && (
                    <div
                      className='text-break'
                      dangerouslySetInnerHTML={{ __html: noticeRichText }}
                    />
                  )}
                  <div className='form'>
                    <div className='flex-row'>
                      <TextInput
                        label={nameLabel + '*'}
                        placeholder={nameLabel}
                        name='name'
                        className='inline-control control input-text w250'
                        labelClassName='control-label'
                        onChange={(e) => handleInputChange(e, 'name')}
                        value={formData.name}
                        error={errors.name}
                      />
                      <TextInput
                        label={surnameLabel + '*'}
                        placeholder={surnameLabel}
                        name='surname'
                        className='inline-control control input-text w250'
                        labelClassName='control-label'
                        onChange={(e) => handleInputChange(e, 'surname')}
                        value={formData.surname}
                        error={errors.surname}
                      />
                    </div>

                    <div
                      className='flex-row align-items-start'>
                      <TextInput
                        label={emailLabel + '*'}
                        placeholder={emailLabel}
                        name='email'
                        className='inline-control control input-text w250'
                        labelClassName='control-label'
                        onChange={(e) => handleInputChange(e, 'email')}
                        value={formData.email}
                        error={errors.email}
                      />

                      <TextInput
                        label={policyNumberLabel + '*'}
                        placeholder={policyNumberLabel}
                        name='policyNumber'
                        className='inline-control control input-text w250'
                        labelClassName='control-label'
                        onChange={(e) => handleInputChange(e, 'policyNumber')}
                        value={formData.policyNumber}
                        error={errors.policyNumber}
                      />
                    </div>

                    <div className='flex-row'>
                      <TextInput
                        label={vinLabel}
                        placeholder={vinLabel}
                        name='vin'
                        className='inline-control control input-text w250'
                        labelClassName='control-label'
                        onChange={(e) => handleInputChange(e, 'vin')}
                        value={formData.vin}
                      />

                      <Tooltip
                        title={t('validation.digit-length-rule', {
                          min: 8,
                          max: 12
                        })}
                        arrow>
                        <div>
                          <TextInput
                            label={phoneNumberLabel}
                            placeholder={phoneNumberLabel}
                            maxLength={12}
                            name='phoneNumber'
                            className='inline-control control input-text w250'
                            labelClassName='control-label'
                            onChange={(e) =>
                              handleInputChange(e, 'phoneNumber')
                            }
                            value={formData.phoneNumber}
                            error={errors.phoneNumber}
                          />
                        </div>
                      </Tooltip>
                    </div>

                    <div className='flex-row'>
                      <TextInput
                        label={withdrawalReasonLabel}
                        placeholder={withdrawalReasonLabel}
                        name='withdrawalReason'
                        className='inline-control control input-text w250'
                        labelClassName='control-label'
                        onChange={(e) =>
                          handleInputChange(e, 'withdrawalReason')
                        }
                        value={formData.withdrawalReason}
                      />
                    </div>

                    <div className='form-nav'>
                      <ButtonContinue
                        name='btnSubmit'
                        text={t('general.submit')}
                        disabled={isFormSubmitted}
                        onClick={handleSubmit}
                      />
                      {submitError && (
                        <div className='m1-top error-style'>
                          {t('withdrawal.submission-error')}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WithdrawalPage;
